import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { setActiveTeamIdGetter } from "@/lib/api";
import { useTeams } from "@/lib/api/teams.queries";
import type { TeamSummary } from "@/lib/api/teams";
import { useAuth } from "@/context/AuthContext";

const ACTIVE_TEAM_KEY = "pahfacturar.app.activeTeamId";

type TeamContextValue = {
  /** Currently selected team id, or null if the user has not picked / has none. */
  activeTeamId: string | null;
  /** Currently selected team summary, or null if the team list has not loaded. */
  activeTeam: TeamSummary | null;
  /** Pick a team. Persisted to localStorage and propagated to lib/api header injection. */
  setActiveTeamId: (teamId: string | null) => void;
  /** Has the team list initial fetch completed yet? */
  ready: boolean;
};

const TeamContext = createContext<TeamContextValue | null>(null);

function readPersisted(): string | null {
  try {
    return localStorage.getItem(ACTIVE_TEAM_KEY);
  } catch {
    return null;
  }
}

function persist(value: string | null) {
  try {
    if (value) localStorage.setItem(ACTIVE_TEAM_KEY, value);
    else localStorage.removeItem(ACTIVE_TEAM_KEY);
  } catch {
    // Storage might be disabled (private mode); silently ignore.
  }
}

/**
 * TeamProvider — single source of truth for the "currently active team".
 *
 * Hydration order:
 *   1. read localStorage on mount (instant — survives reloads)
 *   2. once `useTeams()` resolves, sanity-check the persisted id is still in
 *      the user's membership list; if not, fall back to user.defaultTeamId,
 *      then to the first team in the list, else null
 *
 * Side effect: registers a getter with lib/api so authenticated fetches
 * automatically attach `X-Team-Id` on company / DIAN-scoped paths.
 */
export function TeamProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const teamsQuery = useTeams();
  // We deliberately DO NOT seed from localStorage on first render. A stale
  // teamId (e.g. left over from a previous session whose D1 row no longer
  // exists, or from a different account) would expose `activeTeamId` while
  // the hydrate effect hasn't run yet — and that's enough for downstream
  // queries like useCompanies to fire with a bad / stale / missing
  // X-Team-Id and 400 NO_TEAM_CONTEXT or 403 NOT_TEAM_MEMBER. We hydrate
  // ONLY after `useTeams` resolves and we can validate the persisted id
  // against the user's actual membership list.
  const [activeTeamId, setActiveTeamIdState] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Wire lib/api to read the active team id on every request. We register the
  // getter ONCE and have it read a ref kept in sync DURING RENDER — NOT a value
  // captured by a `[activeTeamId]` effect. This timing is load-bearing: the
  // company/DIAN/subscription query hooks gate on `!!activeTeamId`, so the
  // instant hydration flips it truthy they become enabled and fire their fetch
  // in a passive effect. Passive effects run child-before-parent, so a
  // descendant query's fetch runs BEFORE this provider's effect — an
  // effect-registered getter would still return the stale `null` and the
  // request would ship with no X-Team-Id. In production the JWT carries no
  // `defaultTeamId`, so the BFF answers 400 NO_TEAM_CONTEXT and (retry:false)
  // the page stays stuck on an error until the next reload. A ref written in
  // render is current before any effect runs, closing that one-commit window.
  const activeTeamIdRef = useRef<string | null>(activeTeamId);
  activeTeamIdRef.current = activeTeamId;
  useEffect(() => {
    setActiveTeamIdGetter(() => activeTeamIdRef.current);
    return () => {
      setActiveTeamIdGetter(() => null);
    };
  }, []);

  // Hydrate the active team once the team list comes in. Validates the
  // persisted id against the user's membership; falls back to the JWT's
  // defaultTeamId, then the first team, else null. Never re-runs once
  // hydrated so a later setActiveTeamId() call from the header switcher
  // can't get clobbered.
  useEffect(() => {
    if (hydrated) return;
    if (auth.status === "unauthenticated") {
      setHydrated(true);
      return;
    }
    if (auth.status !== "authenticated") return;
    if (teamsQuery.status !== "success") return;
    const teams = teamsQuery.data ?? [];
    const knownIds = new Set(teams.map((t) => t.id));
    const persisted = readPersisted();
    let next: string | null;
    if (persisted && knownIds.has(persisted)) {
      next = persisted;
    } else {
      next =
        (auth.user.defaultTeamId && knownIds.has(auth.user.defaultTeamId)
          ? auth.user.defaultTeamId
          : null) ??
        teams[0]?.id ??
        null;
    }
    if (next !== persisted) persist(next);
    setActiveTeamIdState(next);
    setHydrated(true);
  }, [hydrated, auth, teamsQuery.status, teamsQuery.data]);

  const setActiveTeamId = useCallback((teamId: string | null) => {
    setActiveTeamIdState(teamId);
    persist(teamId);
  }, []);

  const activeTeam = useMemo<TeamSummary | null>(() => {
    if (!activeTeamId) return null;
    const teams = teamsQuery.data ?? [];
    return teams.find((t) => t.id === activeTeamId) ?? null;
  }, [activeTeamId, teamsQuery.data]);

  const value = useMemo<TeamContextValue>(
    () => ({
      activeTeamId,
      activeTeam,
      setActiveTeamId,
      // `ready` gates downstream queries (useCompanies, useSubscriptions, …).
      // It MUST stay false until hydration resolves activeTeamId to a real
      // value (or definitively null) — otherwise downstream queries fire
      // with a stale/empty X-Team-Id header.
      ready: hydrated,
    }),
    [activeTeamId, activeTeam, setActiveTeamId, hydrated],
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useActiveTeam(): TeamContextValue {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error("useActiveTeam must be used inside TeamProvider");
  return ctx;
}

/**
 * Whether the active team role may create or edit companies: owner, admin or
 * accountant, the roles POST/PATCH /api/me/companies and MCP company_write
 * accept. The API enforces the rule; this only keeps those controls away from
 * viewers.
 */
export function useCanWriteCompanies(): boolean {
  const team = useActiveTeam().activeTeam;
  if (team?.permissions !== undefined) return false;
  const role = team?.role;
  return role === "owner" || role === "admin" || role === "accountant";
}

/**
 * Whether the active team role may make a company's lifecycle changes: owner
 * or admin, the roles the API accepts to archive a company (DELETE
 * /api/me/companies/:id) and to change its logo (PUT/DELETE
 * .../branding/logo). Accountants and viewers don't get these controls.
 */
export function useCanAdministerCompanies(): boolean {
  const role = useActiveTeam().activeTeam?.role;
  return role === "owner" || role === "admin";
}

/**
 * Whether the active team role may delete a company's DIAN certificate:
 * owner only, as DELETE /api/me/companies/:id/cert requires.
 */
export function useCanDeleteCertificate(): boolean {
  return useActiveTeam().activeTeam?.role === "owner";
}

/**
 * Whether the active team role may give or revoke a tax declaration's visto
 * bueno: owner or admin, as POST/DELETE
 * /api/me/companies/:id/taxes/declarations/:eventId/approval require (PF-388).
 * The draft's filingGate.approvable describes the draft, not the caller.
 */
export function useCanApproveTaxDeclarations(): boolean {
  const role = useActiveTeam().activeTeam?.role;
  return role === "owner" || role === "admin";
}

export function useTeamPermission(permission: import("@facturar/auth").TeamPermission): boolean {
  const permissions = useActiveTeam().activeTeam?.permissions;
  return permissions === undefined || permissions.includes(permission);
}
